import i18n, { InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import EnUsTranslations from './locales/en-US/translations';
import NlNlTranslations from './locales/nl-NL/translations';
import TrTrTranslations from './locales/tr-TR/translations';
import DeDeTranslations from './locales/de-DE/translations';
import EsEsTranslations from './locales/es-ES/translations';
import FrFrTranslations from './locales/fr-FR/translations';
import ItItTranslations from './locales/it-IT/translations';
import PtPtTranslations from './locales/pt-PT/translations';
import EnUsTermsTranslations from './locales/en-US/terms.translations';
import NlNlTermsTranslations from './locales/nl-NL/terms.translations';
import TrTrTermsTranslations from './locales/tr-TR/terms.translations';
import DeDeTermsTranslations from './locales/de-DE/terms.translations';
import EsEsTermsTranslations from './locales/es-ES/terms.translations';
import FrFrTermsTranslations from './locales/fr-FR/terms.translations';
import ItItTermsTranslations from './locales/it-IT/terms.translations';
import PtPtTermsTranslations from './locales/pt-PT/terms.translations';
import EnUsManagementTranslations from './locales/en-US/management.translations';
import NlNlManagementTranslations from './locales/nl-NL/management.translations';
import TrTrManagementTranslations from './locales/tr-TR/management.translations';
import DeDeManagementTranslations from './locales/de-DE/management.translations';
import EsEsManagementTranslations from './locales/es-ES/management.translations';
import FrFrManagementTranslations from './locales/fr-FR/management.translations';
import ItItManagementTranslations from './locales/it-IT/management.translations';
import PtPtManagementTranslations from './locales/pt-PT/management.translations';
import TrTrNotificationsTranslations from './locales/tr-TR/notifications.translations';
import EnUsNotificationsTranslations from './locales/en-US/notifications.translations';
import NlNlNotificationsTranslations from './locales/nl-NL/notifications.translations';
import DeDeNotificationsTranslations from './locales/de-DE/notifications.translations';
import EsEsNotificationsTranslations from './locales/es-ES/notifications.translations';
import FrFrNotificationsTranslations from './locales/fr-FR/notifications.translations';
import ItItNotificationsTranslations from './locales/it-IT/notifications.translations';
import PtPtNotificationsTranslations from './locales/pt-PT/notifications.translations';
import LOCALES, { ILocale } from './locales';
import { getCookieObject } from './hooks/useCookie';
import { COOKIE_LOCALE } from './constants';

export const FALLBACK_LOCALE = LOCALES.en_NL;

const config: InitOptions = {
  resources: {
    [LOCALES.tr_TR.code]: {
      translations: TrTrTranslations,
      terms: TrTrTermsTranslations,
      management: TrTrManagementTranslations,
      notifications: TrTrNotificationsTranslations,
    },
    [LOCALES.en_NL.code]: {
      translations: EnUsTranslations,
      terms: EnUsTermsTranslations,
      management: EnUsManagementTranslations,
      notifications: EnUsNotificationsTranslations,
    },
    [LOCALES.nl_NL.code]: {
      translations: NlNlTranslations,
      terms: NlNlTermsTranslations,
      management: NlNlManagementTranslations,
      notifications: NlNlNotificationsTranslations,
    },
    [LOCALES.de_DE.code]: {
      translations: DeDeTranslations,
      terms: DeDeTermsTranslations,
      management: DeDeManagementTranslations,
      notifications: DeDeNotificationsTranslations,
    },
    [LOCALES.es_ES.code]: {
      translations: EsEsTranslations,
      terms: EsEsTermsTranslations,
      management: EsEsManagementTranslations,
      notifications: EsEsNotificationsTranslations,
    },
    [LOCALES.fr_FR.code]: {
      translations: FrFrTranslations,
      terms: FrFrTermsTranslations,
      management: FrFrManagementTranslations,
      notifications: FrFrNotificationsTranslations,
    },
    [LOCALES.it_IT.code]: {
      translations: ItItTranslations,
      terms: ItItTermsTranslations,
      management: ItItManagementTranslations,
      notifications: ItItNotificationsTranslations,
    },
    [LOCALES.pt_PT.code]: {
      translations: PtPtTranslations,
      terms: PtPtTermsTranslations,
      management: PtPtManagementTranslations,
      notifications: PtPtNotificationsTranslations,
    },
    [LOCALES.en_US.code]: {
      translations: EnUsTranslations,
      terms: EnUsTermsTranslations,
      management: EnUsManagementTranslations,
      notifications: EnUsNotificationsTranslations,
    },
  },
  supportedLngs: [
    'en',
    LOCALES.tr_TR.code,
    LOCALES.en_NL.code,
    LOCALES.nl_NL.code,
    LOCALES.de_DE.code,
    LOCALES.es_ES.code,
    LOCALES.fr_FR.code,
    LOCALES.it_IT.code,
    LOCALES.pt_PT.code,
    LOCALES.en_US.code,
  ],
  load: 'currentOnly',
  debug: false,
  ns: ['translations', 'terms', 'management', 'download', 'notifications'],
  defaultNS: 'translations',
  interpolation: {
    escapeValue: false,
  },
};

const instance = i18n.createInstance();

export function createSSRClient(lng: string) {
  instance.use(LanguageDetector).init({
    ...config,
    detection: {
      order: ['path', 'cookie'],
      lookupFromPathIndex: 0,
      lookupCookie: COOKIE_LOCALE,
    },
    lng,
    fallbackLng: lng,
  });

  return instance;
}

const getLocaleCodeFromCookie = () =>
  getCookieObject()?.[COOKIE_LOCALE] ?? null;

export const getCurrentLocaleByPathname = (pathname: string) =>
  getCurrentLocaleByCode(getCurrentLocaleCodeByPathname(pathname));

export const getCurrentLocaleByCode = (localePath: string) => {
  return (
    LOCALES[localePath.replace('-', '_') as keyof typeof LOCALES] ||
    LOCALES.en_NL
  );
};

export const getCurrentLocaleCodeByPathname = (pathname: string) => {
  const match = pathname.match(/(\w\w-\w\w)/i);
  if (match?.[0]) {
    return match[0];
  }
  return getLocaleCodeFromCookie() ?? FALLBACK_LOCALE.code;
};

export const formatMoney = (
  amount: number,
  currentLocale: ILocale,
  currency?: string,
) => {
  return Intl.NumberFormat(currentLocale.code, {
    maximumSignificantDigits: 3,
    currency: currency || currentLocale.currency,
    style: 'currency',
  }).format(amount);
};

export default instance;
